// src/prototype/components/EAJourneyBar.tsx
// ============================================================
// EA PROTOTYPE JOURNEY BAR v9
// UPDATED: Added HelpPopover icons to each journey step
// UPDATED: Uses isPro prop for plan-based feature gating
// UPDATED: Step 2 shows "5 use cases" badge for freemium
// UPDATED: Step 3 locked for freemium, unlocked for pro
// 
// 3-Step Flow:
//   1. Personalize → Your Inputs (all users)
//   2. Evaluate → Review & Select Finalists (freemium: 5 use cases, pro: all)
//   3. Execute → Champion & Initiate (pro only)
// ============================================================

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Sliders,
  Lightbulb,
  Rocket,
  CheckCircle2,
  Lock
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { EAPersonalizationWidget } from './EAPersonalizationWidget';
import { useDemoPlan } from '../../hooks/useDemoPlan';
import { HelpPopover } from './help/HelpPopover';
import type { HelpKey } from '../help/helpContent';
import {
  calculateOverallPersonalizationStrength,
  initiativesScoreFromFinalistCount,
} from '../utils/personalizationProgress';
import { EA_CLIENT_ID } from '../constants/eaClient';
import { useEAPersonalizationProgress } from '../hooks/useEAPersonalizationProgress';
import { useEAOptional } from '../contexts/EAContext';

interface EAJourneyBarProps {
  onStepClick?: (stepId: number) => void;
  /**
   * @deprecated Ignored. The bar always loads Personalization Strength for
   * EA_CLIENT_ID so Profile / ROI / Rationale cannot drift.
   */
  personalizationData?: {
    profile?: number;
    surveys?: number;
    documents?: number;
    initiatives?: number;
  };
  /** @deprecated Ignored — loading comes from the shared progress hook. */
  progressLoading?: boolean;
  /** @deprecated - Now uses useDemoPlan hook directly */
  isPro?: boolean;
}

// Step-specific descriptions - Updated for 3-step flow
const EA_STEP_DESCRIPTIONS: Record<string, { thesis: string; explanation: string }> = {
  personalize: {
    thesis: "Personalization shapes the intelligence you see.",
    explanation:
      "Your organizational inputs directly influence which opportunities surface, how they're evaluated, and how defensible the results are. Improving personalization increases relevance and confidence.",
  },
  evaluate: {
    thesis: "Evaluate opportunities and select your finalists.",
    explanation:
      "Review why each opportunity was identified, compare ROI projections, flag assumptions, and mark your top choices for approval. Your flags help us refine future projections.",
  },
  approve: {
    thesis: "Champion your initiatives and initiate execution.",
    explanation:
      "Access approval-ready materials, prioritize your initiatives, and prepare to defend your recommendations to leadership. Your execution kits are ready for leadership review.",
  },
};

const eaSteps = [
  {
    id: 1,
    label: 'Personalize',
    sublabel: 'Your Inputs',
    icon: Sliders,
    route: '/ea/personalization',
    tier: 'all', // Available to all users
    descriptionKey: 'personalize',
    helpKey: 'journey_personalize' as HelpKey,
  },
  {
    id: 2,
    label: 'Evaluate',
    sublabel: 'Review & Select Finalists',
    icon: Lightbulb,
    route: '/ea/opportunities',
    tier: 'limited', // Freemium: 5 use cases, Pro: all
    descriptionKey: 'evaluate',
    helpKey: 'journey_evaluate_shortlist' as HelpKey,
  },
  {
    id: 3,
    label: 'Execute',
    sublabel: 'Champion & Initiate',
    icon: Rocket,
    route: '/ea/activate',
    tier: 'pro', // Pro only
    descriptionKey: 'approve',
    helpKey: 'journey_approval_execute' as HelpKey,
  },
];

export function EAJourneyBar({ onStepClick }: EAJourneyBarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [hoveredStep, setHoveredStep] = useState<number | null>(null);

  // Use the unified demo plan hook directly - this is the source of truth
  const { isPro } = useDemoPlan();
  const ea = useEAOptional();
  const initiatives = initiativesScoreFromFinalistCount(ea?.finalistIds.size ?? 0);
  const { personalizationData, progressLoading } = useEAPersonalizationProgress(
    EA_CLIENT_ID,
    initiatives,
  );

  console.log('🎯 EAJourneyBar - isPro:', isPro, 'path:', location.pathname);

  // Determine current step from route (updated for 3-step flow)
  const getCurrentStep = (): number => {
    const path = location.pathname;
    // Step 3: Get Approval & Execute (includes command center drilldown)
    if (path.includes('/ea/activate') || path.includes('/ea/validation') || path.includes('/ea/execute')) return 3;
    // Step 2: Evaluate & related views (profile, rationale, ROI)
    if (
      path.includes('/ea/opportunities') ||
      path.includes('/ea/decisions') ||
      path.includes('/ea/roi-model') ||
      path.includes('/ea/use-case') ||
      path.includes('/ea/weights')
    ) return 2;
    // Upgrade page shows as between step 2 and 3
    if (path.includes('/ea/upgrade')) return 2.5;
    // Default: Step 1 Personalize
    return 1;
  };

  const currentStep = getCurrentStep();

  // Get current step's description key
  const getCurrentDescriptionKey = (): string => {
    const path = location.pathname;
    if (path.includes('/ea/activate') || path.includes('/ea/validation') || path.includes('/ea/execute')) return 'approve';
    if (
      path.includes('/ea/opportunities') ||
      path.includes('/ea/decisions') ||
      path.includes('/ea/roi-model') ||
      path.includes('/ea/use-case') ||
      path.includes('/ea/weights') ||
      path.includes('/ea/upgrade')
    ) return 'evaluate';
    return 'personalize';
  };

  const currentDescriptionKey = getCurrentDescriptionKey();
  const currentDescription = EA_STEP_DESCRIPTIONS[currentDescriptionKey];

  // Overall = equal-weight average of profile, surveys, documents, and initiatives
  const overallPercentage = personalizationData
    ? calculateOverallPersonalizationStrength(personalizationData)
    : 0;

  const handleStepClick = (step: typeof eaSteps[0]) => {
    console.log('🚀 EAJourneyBar handleStepClick:', {
      stepId: step.id,
      stepLabel: step.label,
      stepTier: step.tier,
      stepRoute: step.route,
      isPro,
      willRedirectToUpgrade: step.tier === 'pro' && !isPro,
    });

    // Pro-only steps redirect to upgrade for freemium users
    if (step.tier === 'pro' && !isPro) {
      console.log('🔒 Redirecting to upgrade page because isPro is false');
      navigate('/ea/upgrade');
      return;
    }

    console.log('✅ Navigating to:', step.route);
    if (onStepClick) {
      onStepClick(step.id);
    }
    navigate(step.route);
  };

  const isStepAccessible = (step: typeof eaSteps[0]): boolean => {
    if (step.tier === 'all') return true;
    if (step.tier === 'limited') return true; // Accessible but limited for freemium
    if (step.tier === 'pro') return isPro;
    return true;
  };

  const isStepCompleted = (stepId: number): boolean => {
    return stepId < Math.floor(currentStep);
  };

  const isStepLocked = (step: typeof eaSteps[0]): boolean => {
    return step.tier === 'pro' && !isPro;
  };

  return (
    <div className="flex items-stretch gap-4">
      {/* Main Journey Bar */}
      <div className="flex-1 bg-white rounded-xl shadow-md border border-[#e5e7eb]">
        {/* ROW A: Stepper */}
        <div className="px-6 pt-3 pb-2">
          <div className="flex items-center justify-between">
            <TooltipProvider delayDuration={100}>
              {eaSteps.map((step, index) => {
                const StepIcon = step.icon;
                const isAccessible = isStepAccessible(step);
                const isCompleted = isStepCompleted(step.id);
                const isCurrent = Math.floor(currentStep) === step.id;
                const isLocked = isStepLocked(step);
                const isLimitedForFreemium = step.tier === 'limited' && !isPro;

                return (
                  <React.Fragment key={step.id}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className={`flex flex-col items-center flex-1 group ${isAccessible ? 'cursor-pointer' : 'cursor-not-allowed'
                            }`}
                          onMouseEnter={() => setHoveredStep(step.id)}
                          onMouseLeave={() => setHoveredStep(null)}
                          onClick={() => handleStepClick(step)}
                          role="button"
                          aria-label={`${step.label} - ${step.sublabel}${isLocked ? ' (Pro feature, locked)' : ''}`}
                        >
                          <div className="relative">
                            {/* Step circle */}
                            <div
                              className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 ${isCompleted
                                ? 'bg-[#18365E] text-white'
                                : isCurrent
                                  ? 'bg-[#FF6E4C] text-white shadow-lg'
                                  : isLocked
                                    ? 'bg-[#F5F7FA] text-[#717182] border-2 border-[#e5e7eb]'
                                    : 'bg-white text-[#18365E] border-2 border-[#18365E]/40'
                                } ${hoveredStep === step.id && isAccessible ? 'scale-110 shadow-xl' : ''}`}
                            >
                              {isCompleted ? (
                                <CheckCircle2 className="w-4 h-4" />
                              ) : isLocked ? (
                                <Lock className="w-3.5 h-3.5" />
                              ) : (
                                <StepIcon className="w-4 h-4" />
                              )}
                            </div>

                            {/* Active step glow */}
                            {isCurrent && (
                              <div className="absolute inset-0 rounded-full bg-[#FF6E4C]/30 animate-ping" />
                            )}

                            {/* Pro badge for Step 3 (locked state) */}
                            {isLocked && (
                              <div className="absolute -top-1 -right-1 px-1 py-0.5 rounded text-[7px] font-bold bg-[#e5e7eb] text-[#717182]">
                                PRO
                              </div>
                            )}

                            {/* "5 use cases" badge for Step 2 freemium users */}
                            {isLimitedForFreemium && !isLocked && (
                              <div className="absolute -top-1 -right-2 px-1.5 py-0.5 rounded text-[7px] font-bold bg-amber-100 text-amber-700 whitespace-nowrap">
                                5 use cases
                              </div>
                            )}
                          </div>

                          {/* Step labels with help icon */}
                          <div className="flex items-center gap-0.5 mt-1.5">
                            <span
                              className={`text-[10px] text-center max-w-[110px] transition-all leading-tight ${isCurrent
                                ? 'text-[#FF6E4C]'
                                : isLocked
                                  ? 'text-[#717182]'
                                  : 'text-[#18365E]'
                                }`}
                              style={{ fontWeight: isCurrent ? 700 : 600 }}
                            >
                              {step.label}
                            </span>
                            {/* Help icon - hidden on small screens, stops propagation to prevent step click */}
                            <span
                              className="hidden sm:inline-flex items-center"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <HelpPopover helpKey={step.helpKey} iconSize={12} />
                            </span>
                          </div>

                          {/* Sublabel */}
                          <span
                            className={`text-[9px] text-center transition-all leading-tight ${isLocked ? 'text-[#717182]/60' : 'text-[#717182]'
                              }`}
                            style={{ fontWeight: 500 }}
                          >
                            {step.sublabel}
                          </span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent
                        side="bottom"
                        className={`${isLocked
                          ? 'bg-[#717182]'
                          : isCurrent
                            ? 'bg-[#FF6E4C]'
                            : 'bg-[#18365E]'
                          } text-white border-0 max-w-xs`}
                      >
                        <p className="text-xs font-medium">
                          {isLocked
                            ? '🔒 Upgrade to Pro to access this step'
                            : isLimitedForFreemium
                              ? `${EA_STEP_DESCRIPTIONS[step.descriptionKey].thesis} (5 use cases in free tier)`
                              : EA_STEP_DESCRIPTIONS[step.descriptionKey].thesis
                          }
                        </p>
                      </TooltipContent>
                    </Tooltip>

                    {/* Connector line */}
                    {index < eaSteps.length - 1 && (
                      <div
                        className={`h-0.5 flex-1 mx-2 mt-[-36px] transition-all duration-500 ${isCompleted
                          ? 'bg-[#18365E]'
                          : 'bg-[#e5e7eb]'
                          }`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </TooltipProvider>
          </div>
        </div>

        {/* ROW B: Journey Context Row */}
        <div className="px-6 py-2 border-t border-[#e5e7eb] bg-[#FAFBFC]">
          <div className="w-full">
            <p className="text-sm font-semibold text-[#18365E] leading-snug">
              {currentDescription.thesis}
            </p>
            <p className="text-xs text-[#717182] leading-snug mt-0.5">
              {currentDescription.explanation}
            </p>
          </div>
        </div>
      </div>

      {/* Personalization Widget - Right Side */}
      <div className="hidden lg:block shrink-0 w-[220px]">
        <EAPersonalizationWidget
          overallPercentage={overallPercentage}
          profilePercentage={personalizationData?.profile || 0}
          surveysPercentage={personalizationData?.surveys || 0}
          documentsPercentage={personalizationData?.documents || 0}
          initiativesPercentage={personalizationData?.initiatives || 0}
          isLoading={progressLoading}
        />
      </div>
    </div>
  );
}

export default EAJourneyBar;